import { supabase } from '../lib/supabase';

export interface UploadPart {
  partNumber: number;
  etag: string;
}

export interface MultipartUploadSession {
  videoId: string;
  uploadId: string;
  storagePath: string;
  fileName: string;
  fileSize: number;
  partSize: number;
  totalParts: number;
  parts: UploadPart[];
}

export interface UploadProgress {
  uploadedBytes: number;
  totalBytes: number;
  percentage: number;
  uploadedParts: number;
  totalParts: number;
  currentPart?: number;
  speed?: number; // bytes per second
  eta?: number; // seconds remaining
}

export interface UploadCallbacks {
  onProgress?: (progress: UploadProgress) => void;
  onPartComplete?: (partNumber: number, etag: string) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

const STORAGE_KEY_PREFIX = 'multipart_upload_';

/**
 * Save upload session to localStorage
 */
export const saveUploadSession = (videoId: string, session: MultipartUploadSession): void => {
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${videoId}`, JSON.stringify(session));
  } catch (error) {
    console.warn('Failed to save upload session to localStorage:', error);
  }
};

/**
 * Load upload session from localStorage
 */
export const loadUploadSession = (videoId: string): MultipartUploadSession | null => {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${videoId}`);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('Failed to load upload session from localStorage:', error);
    return null;
  }
};

/**
 * Clear upload session from localStorage
 */
export const clearUploadSession = (videoId: string): void => {
  try {
    localStorage.removeItem(`${STORAGE_KEY_PREFIX}${videoId}`);
  } catch (error) {
    console.warn('Failed to clear upload session from localStorage:', error);
  }
};

/**
 * Initiate multipart upload
 */
export const initiateMultipartUpload = async (
  videoId: string,
  file: File
): Promise<MultipartUploadSession> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Not authenticated');
  }

  const contentType = file.type || 'video/mp4';
  const fileName = file.name;

  const { data, error } = await supabase.functions.invoke('initiate-multipart-upload', {
    body: {
      videoId,
      fileName,
      contentType,
      fileSize: file.size,
    },
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
  });

  if (error) {
    throw new Error(error.message || 'Failed to initiate multipart upload');
  }

  if (!data || !data.uploadId || !data.storagePath) {
    throw new Error('Invalid response from server');
  }

  const uploadSession: MultipartUploadSession = {
    videoId,
    uploadId: data.uploadId,
    storagePath: data.storagePath,
    fileName,
    fileSize: file.size,
    partSize: data.partSize,
    totalParts: data.totalParts,
    parts: [],
  };

  saveUploadSession(videoId, uploadSession);
  return uploadSession;
};

/**
 * Get presigned URL for a specific part
 */
const getPartUrl = async (
  videoId: string,
  storagePath: string,
  uploadId: string,
  partNumber: number
): Promise<string> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Not authenticated');
  }

  const { data, error } = await supabase.functions.invoke('get-upload-part-url', {
    body: {
      videoId,
      storagePath,
      uploadId,
      partNumber,
    },
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
  });

  if (error) {
    throw new Error(error.message || 'Failed to get part URL');
  }

  if (!data || !data.url) {
    throw new Error('Invalid response from server');
  }

  return data.url;
};

/**
 * Upload a single part
 */
const uploadPart = async (
  partUrl: string,
  partData: Blob,
  partNumber: number
): Promise<string> => {
  const response = await fetch(partUrl, {
    method: 'PUT',
    body: partData,
    headers: {
      'Content-Type': 'application/octet-stream',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to upload part ${partNumber}: ${errorText}`);
  }

  // Extract ETag from response headers
  const etag = response.headers.get('ETag')?.replace(/"/g, '') || '';
  if (!etag) {
    throw new Error(`No ETag received for part ${partNumber}`);
  }

  return etag;
};

/**
 * Complete multipart upload
 */
const completeMultipartUpload = async (
  videoId: string,
  storagePath: string,
  uploadId: string,
  parts: UploadPart[]
): Promise<void> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Not authenticated');
  }

  // Sort parts by partNumber
  const sortedParts = [...parts].sort((a, b) => a.partNumber - b.partNumber);

  const { data, error } = await supabase.functions.invoke('complete-multipart-upload', {
    body: {
      videoId,
      storagePath,
      uploadId,
      parts: sortedParts,
    },
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
  });

  if (error) {
    throw new Error(error.message || 'Failed to complete multipart upload');
  }

  if (!data || !data.success) {
    throw new Error('Invalid response from server');
  }
};

/**
 * Upload file using multipart upload
 */
export const uploadFileMultipart = async (
  file: File,
  videoId: string,
  callbacks: UploadCallbacks = {}
): Promise<void> => {
  const { onProgress, onPartComplete, onError, onComplete } = callbacks;

  try {
    // Check for existing session
    let session = loadUploadSession(videoId);
    let startPart = 1;

    if (session) {
      // Resume from existing session
      startPart = session.parts.length + 1;
    } else {
      // Initiate new upload
      session = await initiateMultipartUpload(videoId, file);
    }

    const { uploadId, storagePath, partSize, totalParts } = session!;

    // Calculate file parts
    const parts: Blob[] = [];
    for (let i = 0; i < totalParts; i++) {
      const start = i * partSize;
      const end = Math.min(start + partSize, file.size);
      parts.push(file.slice(start, end));
    }

    // Upload parts sequentially (to avoid overwhelming the browser)
    const uploadedParts: UploadPart[] = [...session!.parts];
    let uploadedBytes = uploadedParts.reduce((sum, part) => sum + partSize, 0);
    const startTime = Date.now();
    let lastProgressTime = startTime;
    let lastProgressBytes = uploadedBytes;

    for (let i = startPart - 1; i < totalParts; i++) {
      const partNumber = i + 1;
      const partData = parts[i];

      // Get presigned URL for this part
      const partUrl = await getPartUrl(videoId, storagePath, uploadId, partNumber);

      // Upload the part
      const etag = await uploadPart(partUrl, partData, partNumber);

      // Store the part
      uploadedParts.push({ partNumber, etag });
      uploadedBytes += partData.size;

      // Update session
      session!.parts = uploadedParts;
      saveUploadSession(videoId, session!);

      // Calculate progress
      const now = Date.now();
      const timeElapsed = (now - lastProgressTime) / 1000; // seconds
      const bytesUploaded = uploadedBytes - lastProgressBytes;
      const speed = timeElapsed > 0 ? bytesUploaded / timeElapsed : 0;
      const remainingBytes = file.size - uploadedBytes;
      const eta = speed > 0 ? remainingBytes / speed : undefined;

      const progress: UploadProgress = {
        uploadedBytes,
        totalBytes: file.size,
        percentage: (uploadedBytes / file.size) * 100,
        uploadedParts: uploadedParts.length,
        totalParts,
        currentPart: partNumber,
        speed,
        eta,
      };

      onProgress?.(progress);
      onPartComplete?.(partNumber, etag);

      lastProgressTime = now;
      lastProgressBytes = uploadedBytes;
    }

    // Complete multipart upload
    await completeMultipartUpload(videoId, storagePath, uploadId, uploadedParts);

    // Clear session
    clearUploadSession(videoId);

    onComplete?.();
  } catch (error) {
    const uploadError = error instanceof Error ? error : new Error(String(error));
    onError?.(uploadError);
    throw uploadError;
  }
};

/**
 * Upload file (single upload for files <= 300 MB)
 */
export const uploadFileSingle = async (
  file: File,
  videoId: string,
  callbacks: UploadCallbacks = {}
): Promise<void> => {
  const { onProgress, onError, onComplete } = callbacks;

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Not authenticated');
    }

    // Get video to extract storage path
    const { data: video, error: videoError } = await supabase
      .from('videos')
      .select('storage_path')
      .eq('id', videoId)
      .single();

    if (videoError || !video) {
      throw new Error('Video not found');
    }

    // Extract videoUuid and orgId from storage path
    // Format: org_xxx/{videoUuid}/temp/
    const pathParts = video.storage_path.split('/');
    const orgIdPart = pathParts[0] || ''; // org_xxx
    const orgId = orgIdPart.replace('org_', '') || '';
    const videoUuid = pathParts[1] || '';
    const fileExtension = file.name.split('.').pop() || 'mp4';

    // Get presigned URL
    const { data: uploadData, error: uploadError } = await supabase.functions.invoke('get-r2-upload-url', {
      body: {
        orgId,
        videoUuid,
        fileExtension,
        contentType: file.type || 'video/mp4',
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (uploadError || !uploadData || !uploadData.url) {
      throw new Error(uploadError?.message || 'Failed to get upload URL');
    }

    // Upload file
    const xhr = new XMLHttpRequest();

    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress: UploadProgress = {
            uploadedBytes: e.loaded,
            totalBytes: e.total,
            percentage: (e.loaded / e.total) * 100,
            uploadedParts: 1,
            totalParts: 1,
          };
          onProgress?.(progress);
        }
      });

      xhr.addEventListener('load', async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // Update video status to "uploaded" after successful upload
          try {
            const { error: updateError } = await supabase
              .from('videos')
              .update({ status: 'uploaded' })
              .eq('id', videoId);
            
            if (updateError) {
              console.error('Failed to update video status after upload:', updateError);
              // Don't fail the upload - status update is secondary
            }
          } catch (statusError) {
            console.error('Error updating video status:', statusError);
            // Don't fail the upload - status update is secondary
          }
          
          onComplete?.();
          resolve();
        } else {
          const error = new Error(`Upload failed with status ${xhr.status}`);
          onError?.(error);
          reject(error);
        }
      });

      xhr.addEventListener('error', () => {
        const error = new Error('Upload failed');
        onError?.(error);
        reject(error);
      });

      xhr.open('PUT', uploadData.url);
      xhr.setRequestHeader('Content-Type', uploadData.contentType || file.type || 'video/mp4');
      xhr.send(file);
    });
  } catch (error) {
    const uploadError = error instanceof Error ? error : new Error(String(error));
    onError?.(uploadError);
    throw uploadError;
  }
};

/**
 * Upload file (automatically chooses single or multipart based on size)
 */
export const uploadFile = async (
  file: File,
  videoId: string,
  callbacks: UploadCallbacks = {}
): Promise<void> => {
  const MULTIPART_THRESHOLD = 300 * 1024 * 1024; // 300 MB

  if (file.size > MULTIPART_THRESHOLD) {
    return uploadFileMultipart(file, videoId, callbacks);
  } else {
    return uploadFileSingle(file, videoId, callbacks);
  }
};

